import { Injectable, NgZone, inject } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { GoGreenPopupComponent } from './components/go-green-popup.component';
import { Popup, UserService } from '@api';
import { SunTaxPopupComponent, SunTaxPopupComponentResponse } from './components/sun-tax-popup.component';
import { firstValueFrom } from 'rxjs';
import { AutopayPopupComponent } from './components/autopay-popup.component';
import { AutopayGoGreenCombinedPopupComponent } from './components/autopay-go-green-combined.component';
import { TfaVerifyEmailPopupComponent } from './components/tfa-verify-email-popup.component';
import { ComponentType } from '@angular/cdk/portal';

interface PopupConfig<C, R> {
  component: ComponentType<C>;
  initFn?: (component: C) => void;
  config: MatDialogConfig;
  handleResponse?: (response: R) => Promise<unknown>;
}

@Injectable({
  providedIn: 'root',
})
export class PopupService {
  private dialog = inject(MatDialog);
  private zone = inject(NgZone);
  private userService = inject(UserService);

  async go(coacct: string) {
    await this.zone.run(async () => {
      let popups: Popup[];

      try {
        popups = await firstValueFrom(this.userService.userPopupCreate({ popupRequest: { coacct } }));
      } catch (error) {
        console.error(error);
        return;
      }

      for (const popup of popups) {
        const popupConfig = this.getPopupConfig(popup);

        if (!popupConfig) {
          continue;
        }

        const dialogRef = this.dialog.open(popupConfig.component, popupConfig.config);

        if (popupConfig.initFn) {
          popupConfig.initFn(dialogRef.componentInstance);
        }

        // fire and forget tracking seen
        void firstValueFrom(
          this.userService.userSetPopupSeenCreate({
            markPopupSeen: {
              id_to_mark_as_seen: popup.id,
            },
          })
        );

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const response = await firstValueFrom(dialogRef.afterClosed());

        if (popupConfig.handleResponse) {
          await popupConfig.handleResponse(response);
        }
      }
    });
  }

  private getPopupConfig(popup: Popup): PopupConfig<unknown, unknown> | undefined {
    switch (popup.id) {
      case 'go-green-auto-pay':
        return this.getGoGreenPopup(popup);
      case 'tax-reminder': {
        const config: PopupConfig<SunTaxPopupComponent, SunTaxPopupComponentResponse> = {
          component: SunTaxPopupComponent,
          config: { maxWidth: '800px' },
          initFn: (component) => {
            component.taxYear.set(popup.tax_year);
          },
          handleResponse: async (response) => {
            if (response === 'dont-show-again') {
              try {
                await firstValueFrom(this.userService.userDismissPopupCreate({ dismissPopup: { id_to_dismiss: 'tax-reminder' } }));
              } catch (error) {
                console.error(error);
              }
            }
          },
        };
        return config;
      }
      case 'tfa-verify-email': {
        const config: PopupConfig<TfaVerifyEmailPopupComponent, void> = {
          component: TfaVerifyEmailPopupComponent,
          config: TfaVerifyEmailPopupComponent.config,
          initFn: (component) => {
            component.email = popup.email ?? '';
          },
        };
        return config;
      }
    }
  }

  private getGoGreenPopup(popup: Popup) {
    if (popup.sub_type === 'go_green') {
      return {
        component: GoGreenPopupComponent,
        config: {
          panelClass: 'modal-no-padding',
        },
      };
    } else if (popup.sub_type === 'auto_pay') {
      return {
        component: AutopayPopupComponent,
        config: {
          panelClass: 'modal-no-padding',
        },
      };
    } else if (popup.sub_type === 'auto_pay_go_green_combined') {
      return {
        component: AutopayGoGreenCombinedPopupComponent,
        config: {
          panelClass: 'modal-no-padding',
        },
      };
    }
  }
}
